import type {
  ArquivoInfo,
  Item,
  LayoutInfo,
  Material,
  Pedido,
  Regras,
} from '../../types/orders';

type UnknownRecord = Record<string, unknown>;

export interface NormalizeOrderOptions {
  role?: string | null;
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asRecord(value: unknown): UnknownRecord {
  return isRecord(value) ? value : {};
}

function getRecord(record: UnknownRecord, key: string): UnknownRecord {
  return asRecord(record[key]);
}

function getArray(record: UnknownRecord, key: string): unknown[] | undefined {
  const value = record[key];
  return Array.isArray(value) ? value : undefined;
}

function parseNumberLike(value: unknown): number {
  return Number.parseFloat(String(value)) || 0;
}

function getCategoryId(value: unknown): number {
  if (isRecord(value)) {
    return Number(value.id) || 0;
  }

  return Number(value);
}

function toNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' ? value : fallback;
}

function toStringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function toNullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function getProdutoCategoria(produto: UnknownRecord): number {
  if (produto.pro_categoria_item) {
    return getCategoryId(produto.pro_categoria_item);
  }
  if (produto.categoria) {
    return getCategoryId(produto.categoria);
  }
  if (produto.categoria_item) {
    return getCategoryId(produto.categoria_item);
  }
  if (produto.produto_categoria_item) {
    return getCategoryId(produto.produto_categoria_item);
  }
  return 0;
}

function normalizeRegras(value: unknown): Regras {
  if (!value) {
    return {} as Regras;
  }

  const regras = asRecord(value);
  return {
    regra_id:
      typeof regras.regra_id === 'number' ? regras.regra_id : null,
    nome_servico: toStringValue(regras.nome_servico),
    descricao: toStringValue(regras.descricao),
    horario_corte: toStringValue(regras.horario_corte),
    largura_maxima_cm: parseNumberLike(regras.largura_maxima_cm),
    altura_maxima_cm: parseNumberLike(regras.altura_maxima_cm),
    diretorio_base_interno: toStringValue(regras.diretorio_base_interno),
    diretorio_base_externo: toStringValue(regras.diretorio_base_externo),
    diretorio_nome: toStringValue(regras.diretorio_nome),
    mensagens: asRecord(regras.mensagens) as unknown as Regras['mensagens'],
    exportacao: asRecord(regras.exportacao) as unknown as Regras['exportacao'],
    padroes_cdr: asRecord(regras.padroes_cdr) as unknown as Regras['padroes_cdr'],
  } as Regras;
}

function normalizeMaterial(item: UnknownRecord): Material | null {
  const materialData = item.material || item.one1_outros || null;
  if (!isRecord(materialData)) {
    return null;
  }

  return {
    ...materialData,
    mat_id: toNumber(
      materialData.mat_id ?? materialData.id ?? materialData.matId,
    ),
    nome: toStringValue(materialData.nome ?? materialData.descricao),
  };
}

function normalizeArquivo(item: UnknownRecord): ArquivoInfo {
  const arquivo = getRecord(item, 'arquivo');
  return {
    id: toNumber(arquivo.id),
    nome: toNullableString(arquivo.nome || arquivo.nome_arq),
    diretorio: toNullableString(arquivo.diretorio),
    sys_save: arquivo.sys_save === true,
    sys_export: arquivo.sys_export === true,
    sys_export_layout: arquivo.sys_export_layout === true,
    created_at: toNullableString(arquivo.created_at),
    entrega_opcao: toNullableString(
      arquivo.entrega_opcao || item.entrega_opcao,
    ),
    horario_corte: toNullableString(arquivo.horario_corte),
  };
}

function normalizeLayout(value: unknown): LayoutInfo | null {
  if (!isRecord(value)) {
    return null;
  }

  return {
    ola_id: toNumber(value.ola_id ?? value.id),
    ola_arquivo: toNullableString(
      value.ola_arquivo ?? value.nome_layout ?? value.nome,
    ),
    ola_url: toNullableString(
      value.ola_url ?? value.url_layout ?? value.url,
    ),
    ola_path: toNullableString(
      value.ola_path ?? value.path ?? value.diretorio_layout,
    ),
    type_file_layout: toNullableString(
      value.type_file_layout ?? value.type_file ?? value.mimetype,
    ),
  };
}

function normalizeLayouts(value: unknown): LayoutInfo[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const layouts: LayoutInfo[] = [];
  for (const entry of value) {
    const normalized = normalizeLayout(entry);
    if (normalized) {
      layouts.push(normalized);
    }
  }
  return layouts;
}

export function collectFlatItemBuckets(obj: unknown): unknown[] | null {
  if (!isRecord(obj)) {
    return null;
  }

  const buckets: unknown[] = [];
  let found = false;

  for (const value of Object.values(obj)) {
    if (!Array.isArray(value) || value.length === 0) {
      continue;
    }

    const sample = value[0];
    if (!isRecord(sample)) {
      continue;
    }

    if (sample.orc_id != null && sample.oit_id != null && sample.orcamento) {
      buckets.push(...value);
      found = true;
    }
  }

  return found ? buckets : null;
}

export function extractOrcList(response: unknown): unknown[] {
  const root = asRecord(response);
  const payload = getRecord(root, 'data');
  const dataWrapper = getRecord(payload, 'data');

  const flat =
    collectFlatItemBuckets(root) ??
    collectFlatItemBuckets(payload) ??
    collectFlatItemBuckets(dataWrapper);

  if (flat) {
    return flat;
  }

  return (
    getArray(payload, 'orcamentos') ??
    getArray(dataWrapper, 'orcamentos') ??
    getArray(root, 'orc') ??
    getArray(payload, 'orc') ??
    getArray(dataWrapper, 'orc') ??
    getArray(root, 'orcamentos') ??
    []
  );
}

export function regroupFlatItems(
  flatItems: readonly unknown[],
  statusId: number,
): UnknownRecord[] {
  const ordersMap = new Map<string, UnknownRecord>();

  for (const rawEntry of flatItems) {
    if (!isRecord(rawEntry) || rawEntry.orc_id == null) {
      continue;
    }

    const orcKey = String(rawEntry.orc_id);
    const itemStatus = getRecord(rawEntry, 'orcamento_itens_status');
    const itemStatusId = itemStatus.oit_status_id ?? statusId;
    const arquivos = getArray(rawEntry, 'arquivos');
    const item: UnknownRecord = {
      oit_id: rawEntry.oit_id,
      oit_qtde: rawEntry.oit_qtde,
      oit_observacao: rawEntry.oit_observacao,
      oit_visible_stage: rawEntry.oit_visible_stage,
      open_only: rawEntry.open_only,
      linked_context: rawEntry.linked_context,
      is_impressao_vinculada: rawEntry.is_impressao_vinculada,
      is_consumo_base: rawEntry.is_consumo_base,
      produto_personalizado_consumo: rawEntry.produto_personalizado_consumo,
      produto: rawEntry.produto,
      one1_outros: rawEntry.one1_outros,
      arquivos: rawEntry.arquivos,
      arquivo: arquivos && arquivos.length > 0 ? arquivos[0] : null,
      status: {
        oit_status_id: itemStatusId,
        descricao: toStringValue(itemStatus.oit_descricao),
      },
      orcamento_itens_historical: rawEntry.orcamento_itens_historical,
      impressora_log: rawEntry.impressora_log,
      estagio: null,
      __etapa_producao__: itemStatusId,
    };

    let pedido = ordersMap.get(orcKey);
    if (!pedido) {
      const orc = getRecord(rawEntry, 'orcamento');
      const orcamentoStatus = getRecord(orc, 'orcamento_status');
      pedido = {
        orc_id: rawEntry.orc_id,
        orc_key_sufix: orc.orc_key_sufix,
        observacao: null,
        nome_arquivo: null,
        valor_total: orc.orc_valor_total ?? '0',
        data_entrega: orc.orc_data_entrega ?? null,
        data_cadastro: orc.orc_data_validade ?? null,
        data_emissao: orc.orc_data_aprovacao ?? null,
        valor_frete: '0',
        forma_pagamento_id: 0,
        valor_desconto: '0',
        cliente: orc.cliente,
        users: orc.users,
        logistica: orc.logistica,
        status_orcamento: orc.orcamento_status,
        ost_id: orcamentoStatus.ost_id,
        status: { id_pagamento: 0, descricao: '' },
        orcamento_layout: orc.orcamento_layout,
        orcamento_historico_status: orc.orcamento_historico_status,
        producao_expedicao: orc.producao_expedicao,
        __etapa_producao__: statusId,
        itens: [],
      };
      ordersMap.set(orcKey, pedido);
    }

    const itens = pedido.itens as UnknownRecord[];
    if (!itens.some((existingItem) => existingItem.oit_id === item.oit_id)) {
      itens.push(item);
    }
  }

  return Array.from(ordersMap.values());
}

export function normalizeItem(
  value: unknown,
  options: NormalizeOrderOptions = {},
): Item {
  const item = asRecord(value);
  const produto = getRecord(item, 'produto');
  const unidade = getRecord(produto, 'unidade');
  const unidadeMedida = getRecord(produto, 'unidade_medida');
  const status = getRecord(item, 'status');
  const produtoTipo = produto.tipo ?? produto.pro_tipo ?? null;
  const etapaProducaoBruta =
    status.oit_status_id ?? item.__etapa_producao__ ?? status.id ?? null;

  const normalized: Item = {
    oit_id: toNumber(item.oit_id),
    quantidade: toNumber(item.quantidade || item.oit_qtde),
    observacao: toStringValue(item.observacao || item.oit_observacao),
    valor_unitario: String(item.valor_unitario ?? ''),
    valor_total: String(item.valor_total ?? ''),
    produto: {
      pro_id: toNumber(produto.pro_id || produto.id),
      nome: toStringValue(produto.nome || produto.pro_nome),
      tipo: toNumber(produtoTipo),
      categoria: getProdutoCategoria(produto),
      quantidade: toNumber(produto.quantidade),
      unidade: {
        umd_id: toNumber(unidade.umd_id || unidadeMedida.umd_id),
        descricao: toStringValue(
          unidade.descricao || unidadeMedida.descricao,
        ),
        sigla: toStringValue(unidade.sigla || unidadeMedida.sigla),
      },
    },
    material: normalizeMaterial(item),
    open_only: item.open_only === true,
    linked_context: isRecord(item.linked_context)
      ? item.linked_context
      : undefined,
    is_impressao_vinculada: item.is_impressao_vinculada === true,
    is_consumo_base: item.is_consumo_base === true,
    produto_personalizado_consumo: Array.isArray(
      item.produto_personalizado_consumo,
    )
      ? item.produto_personalizado_consumo
      : undefined,
    status: {
      oit_status_id: toNumber(etapaProducaoBruta ?? status.id),
      descricao: toStringValue(status.descricao),
    },
    estagio: toNullableString(item.estagio),
    arquivo: normalizeArquivo(item),
    regras: item.regras ? normalizeRegras(item.regras) : undefined,
    orcamento_layout: normalizeLayouts(item.orcamento_layout),
  };

  if (options.role !== 'producao') {
    normalized.regras = undefined;
  }
  if (Array.isArray(item.objetos)) {
    normalized.objetos = item.objetos;
  }
  if (item.progresso && isRecord(item.progresso)) {
    normalized.progresso = item.progresso;
  }
  if (etapaProducaoBruta != null) {
    normalized.__etapa_producao__ = toNumber(etapaProducaoBruta);
    if (!normalized.status.oit_status_id) {
      normalized.status = {
        ...normalized.status,
        oit_status_id: toNumber(etapaProducaoBruta),
      };
    }
  }

  return normalized;
}

export function normalizePedido(
  value: unknown,
  options: NormalizeOrderOptions = {},
): Pedido {
  const source = asRecord(value);
  const statusOrcamento = getRecord(source, 'status_orcamento');
  const sourceStatus = getRecord(source, 'status');
  const statusPagamento = getRecord(source, 'status_pagamento');
  const cliente = getRecord(source, 'cliente');
  const ostId =
    statusOrcamento.id ?? source.ost_id ?? sourceStatus.ost_id ?? null;
  const statusPagamentoId =
    statusPagamento.id ?? sourceStatus.id_pagamento ?? 0;
  const statusPagamentoDesc =
    statusPagamento.descricao ?? sourceStatus.descricao ?? '';
  const statusPagamentoDescPag =
    statusPagamento.descricao_pagamento ??
    sourceStatus.descricao_pagamento ??
    null;

  const pedido: Pedido = {
    orc_id: toNumber(source.orc_id),
    observacao: toNullableString(source.observacao),
    nome_arquivo: toNullableString(source.nome_arquivo),
    valor_total: String(source.valor_total ?? ''),
    data_entrega: toNullableString(source.data_entrega),
    data_cadastro: toNullableString(source.data_cadastro),
    data_emissao: toNullableString(source.data_emissao),
    valor_frete: String(source.valor_frete ?? ''),
    forma_pagamento_id: toNumber(source.forma_pagamento_id),
    valor_desconto: String(source.valor_desconto ?? ''),
    cliente: {
      cli_id: toNumber(cliente.cli_id),
      nome: toStringValue(cliente.nome ?? cliente.cli_nome),
      razao_social: toStringValue(
        cliente.razao_social ?? cliente.cli_razao_social,
      ),
      nome_fantasia: toStringValue(
        cliente.nome_fantasia ?? cliente.cli_nome,
      ),
      whatsapp: toNullableString(cliente.whatsapp ?? cliente.cli_whatsapp),
    },
    status: {
      id_pagamento: toNumber(statusPagamentoId),
      descricao: toStringValue(statusPagamentoDesc),
      descricao_pagamento: toNullableString(statusPagamentoDescPag),
    },
    itens: Array.isArray(source.itens)
      ? source.itens.map((item) => normalizeItem(item, options))
      : Array.isArray(source.orcamento_itens)
        ? source.orcamento_itens.map((item) => normalizeItem(item, options))
        : [],
    orcamento_layout: normalizeLayouts(source.orcamento_layout),
  };

  if (ostId != null) {
    pedido.ost_id = toNumber(ostId);
  }
  if (source.__etapa_producao__ != null) {
    pedido.__etapa_producao__ = toNumber(source.__etapa_producao__);
  }

  return pedido;
}
